import React, { useEffect, useMemo, useCallback, useRef, useState } from 'react';
import {
  Box,
  Typography
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getColumnsPaginated } from '../../reducers/kanban/columnActions';
import { getTicketsByColumn } from '../../reducers/kanban/ticketActions';
import { useToken } from '../../hooks/useToken';
import { GET_COLUMNS_ERROR } from '../../reducers/kanban/kanbanTypes';
import KanbanRefreshButton from '../../components/apps/kanban/KanbanRefreshButton';
import { client as axios } from '../../axios/client';

/**
 * Vista Kanban para tableros de flujo de bots
 * @param {string} boardId - ID del tablero
 * @param {boolean} isDetailView - Si es vista detallada
 * @param {object} board - Datos del tablero (opcional)
 */
const FlujoBotKanbanView = ({ boardId, isDetailView = false, board = null }) => {
  const dispatch = useDispatch();
  const { getTokenSilent } = useToken();

  const isMountedRef = useRef(true);
  const hasLoadedColumnsRef = useRef(false);

  // Estados para el botón de actualización
  const [isRefreshDisabled, setIsRefreshDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const debugLog = useCallback((...args) => {
    // Debug logging disabled
  }, []);

  // Selectores de Redux
  const allColumns = useSelector(state => state.kanban.columns.items || []);
  const allTickets = useSelector(state => state.kanban.tickets.items || []);

  const showNotification = useCallback((message, severity = 'info') => {
    debugLog(`[Notification ${severity}]: ${message}`);
  }, [debugLog]);

  // Función para actualizar los datos del tablero
  const handleRefresh = useCallback(async () => {
    if (isRefreshDisabled || !boardId) return;

    setIsRefreshDisabled(true);
    setCountdown(30);

    try {
      debugLog('Actualizando datos del tablero...');
      const token = await getTokenSilent();

      if (!token) {
        showNotification('No se pudo obtener el token de autenticación', 'error');
        return;
      }

      // Limpiar el estado de tickets antes de cargar nuevos datos
      dispatch({
        type: 'GET_TICKETS_REQUEST'
      });

      // Paso 1: Recargar columnas
      debugLog(`Recargando columnas para boardId: ${boardId}`);
      let columnsResult;
      try {
        columnsResult = await dispatch(getColumnsPaginated(boardId, 0, 5, token));
      } catch (columnsError) {
        showNotification(`Error al recargar columnas: ${columnsError.message || 'Error desconocido'}`, 'error');
        return;
      }

      // Obtener las columnas del resultado de la petición (no de la closure estale)
      const fetchedColumns = columnsResult?.payload || [];
      const updatedColumnsForBoard = fetchedColumns.filter(col => String(col.board_id) === String(boardId));
      debugLog(`Se encontraron ${updatedColumnsForBoard.length} columnas para el tablero ${boardId}`);

      if (updatedColumnsForBoard.length === 0) {
        showNotification('No se encontraron columnas para este tablero', 'warning');
        return;
      }

      // Paso 2: Cargar tickets para cada columna individualmente
      debugLog('Cargando tickets para cada columna individualmente...');
      let ticketLoadErrors = 0;

      for (let i = 0; i < updatedColumnsForBoard.length; i++) {
        const column = updatedColumnsForBoard[i];
        const columnId = column.id;

        debugLog(`Obteniendo tickets para columna ${column.name} (ID: ${columnId})...`);

        try {
          // Hacer una solicitud directa a la API para obtener los tickets de esta columna
          const response = await axios.get(`/kanban/columns/${columnId}/tickets`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          // Verificar la respuesta
          if (!response.data) {
            throw new Error(`No se recibieron datos del servidor para la columna ${columnId}`);
          }

          // Extraer los tickets de la respuesta
          let columnTickets = [];
          if (response.data.items && Array.isArray(response.data.items)) {
            columnTickets = response.data.items;
          } else if (Array.isArray(response.data)) {
            columnTickets = response.data;
          }

          debugLog(`Columna ${column.name} (ID: ${columnId}): ${columnTickets.length} tickets obtenidos`);

          // Actualizar el estado de Redux con los tickets de esta columna
          dispatch({
            type: 'GET_TICKETS_SUCCESS',
            payload: {
              columnId: columnId,
              tickets: columnTickets
            }
          });

          // Pequeña pausa entre actualizaciones
          if (i < updatedColumnsForBoard.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } catch (ticketError) {
          ticketLoadErrors++;
          debugLog(`Error al cargar tickets para columna ${columnId}: ${ticketError.message}`);
        }
      }

      if (ticketLoadErrors > 0) {
        showNotification(`Datos actualizados con ${ticketLoadErrors} errores en la carga de tickets`, 'warning');
      } else {
        showNotification('Datos actualizados correctamente', 'success');
      }

      debugLog('Actualización de datos completada');
    } catch (error) {
      console.error('Error al actualizar datos:', error);
      showNotification(`Error al actualizar datos: ${error.message || 'Error desconocido'}`, 'error');
    } finally {
      // Iniciar cuenta regresiva
      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setIsRefreshDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

  }, [isRefreshDisabled, boardId, getTokenSilent, dispatch, debugLog, showNotification]);

  // Carga inicial de columnas y tickets para el boardId actual
  useEffect(() => {
    if (!boardId) {
      debugLog('No boardId proporcionado, no se cargarán columnas');
      return;
    }

    // Usamos una referencia para evitar cargas duplicadas
    if (hasLoadedColumnsRef.current) {
      debugLog(`Ya se ha intentado cargar datos para boardId: ${boardId}. No se recargarán.`);
      return;
    }

    // Marcar que ya se ha intentado cargar columnas para este tablero
    hasLoadedColumnsRef.current = true;

    const fetchBoardData = async () => {
      try {
        debugLog(`Obteniendo token para cargar datos del tablero: ${boardId}`);
        const token = await getTokenSilent();

        if (!token) {
          showNotification('No se pudo obtener el token de autenticación', 'error');
          return;
        }

        if (!isMountedRef.current) {
          debugLog('Componente desmontado antes de cargar datos');
          return;
        }

        // Paso 1: Cargar las columnas
        debugLog(`Despachando getColumns para boardId: ${boardId}`);
        dispatch({ type: 'GET_TICKETS_REQUEST' });

        const columnsResult = await dispatch(getColumnsPaginated(boardId, 0, 5, token));

        if (!isMountedRef.current) return;

        debugLog('Columnas cargadas correctamente');

        // Obtener las columnas del resultado (no de la closure estale de allColumns)
        const fetchedColumns = columnsResult?.payload || [];
        const updatedColumnsForBoard = fetchedColumns.filter(col =>
          col && col.board_id && String(col.board_id) === String(boardId)
        );
        debugLog(`Se encontraron ${updatedColumnsForBoard.length} columnas para el tablero ${boardId}`);

        if (updatedColumnsForBoard.length === 0) {
          debugLog('No se encontraron columnas para este tablero');
          return;
        }

        // Paso 2: Cargar tickets para cada columna individualmente
        debugLog('Cargando tickets para cada columna individualmente...');
        let ticketLoadErrors = 0;

        for (let i = 0; i < updatedColumnsForBoard.length; i++) {
          if (!isMountedRef.current) return;

          const column = updatedColumnsForBoard[i];
          const columnId = column.id;

          debugLog(`Obteniendo tickets para columna ${column.name} (ID: ${columnId})...`);

          try {
            // Hacer una solicitud directa a la API para obtener los tickets de esta columna
            const response = await axios.get(`/kanban/columns/${columnId}/tickets`, {
              headers: { Authorization: `Bearer ${token}` }
            });

            if (!isMountedRef.current) return;

            // Verificar la respuesta
            if (!response.data) {
              throw new Error(`No se recibieron datos del servidor para la columna ${columnId}`);
            }

            // Extraer los tickets de la respuesta
            let columnTickets = [];
            if (response.data.items && Array.isArray(response.data.items)) {
              columnTickets = response.data.items;
            } else if (Array.isArray(response.data)) {
              columnTickets = response.data;
            }

            debugLog(`Columna ${column.name} (ID: ${columnId}): ${columnTickets.length} tickets obtenidos`);

            // Actualizar el estado de Redux con los tickets de esta columna
            dispatch({
              type: 'GET_TICKETS_SUCCESS',
              payload: {
                columnId: columnId,
                tickets: columnTickets
              }
            });

            // Pequeña pausa entre actualizaciones
            if (i < updatedColumnsForBoard.length - 1 && isMountedRef.current) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          } catch (ticketError) {
            if (!isMountedRef.current) return;

            ticketLoadErrors++;
            debugLog(`Error al cargar tickets para columna ${columnId}: ${ticketError.message}`);

            // Intentar con getTicketsByColumn como fallback
            try {
              dispatch(getTicketsByColumn(columnId, token));
            } catch (fallbackError) {
              debugLog(`Error en fallback para columna ${columnId}: ${fallbackError.message}`);
            }
          }
        }

        if (ticketLoadErrors > 0) {
          debugLog(`Carga completada con ${ticketLoadErrors} errores`);
        } else {
          debugLog('Carga de tickets completada correctamente');
        }

        debugLog('Carga inicial de datos completada');
      } catch (error) {
        if (!isMountedRef.current) return;

        console.error('Error al cargar datos del tablero:', error);
        showNotification(`Error al cargar datos: ${error.message || 'Error desconocido'}`, 'error');

        dispatch({
          type: GET_COLUMNS_ERROR,
          payload: error.message || 'Error al cargar las columnas del tablero'
        });
      }
    };

    fetchBoardData();
    // Nota: Aunque allColumns se usa dentro de fetchBoardData, no lo incluimos como dependencia
    // para evitar un bucle infinito, ya que fetchBoardData modifica allColumns a través de dispatch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId, dispatch, getTokenSilent, showNotification, debugLog]);

  // Reiniciar el estado cuando cambia el boardId
  useEffect(() => {
    // Reiniciar el estado cuando cambia el boardId
    hasLoadedColumnsRef.current = false;

    return () => {
      // Limpiar cuando se desmonta o cambia el boardId
      hasLoadedColumnsRef.current = false;
    };
  }, [boardId]);

  // Limpiar recursos al desmontar
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      debugLog('Componente desmontado, limpiando recursos');
      isMountedRef.current = false;
    };
  }, [debugLog]);

  const formattedColumns = useMemo(() => {
    if (!boardId) {
      return [];
    }

    debugLog(`Recalculando columnas. boardId: ${boardId}, allColumns: ${allColumns.length}, allTickets: ${allTickets.length}`);

    // Filtrar columnas para el tablero actual
    const columnsForCurrentBoard = allColumns.filter(column =>
      column && column.board_id && String(column.board_id) === String(boardId)
    );
    debugLog(`Columnas para boardId ${boardId}: ${columnsForCurrentBoard.length}`);

    // Si no hay columnas para este tablero, devolver un array vacío
    if (columnsForCurrentBoard.length === 0) {
      return [];
    }

    // Intentar filtrar columnas de tipo flujo_bot
    const flujoBotColumns = columnsForCurrentBoard.filter(column => {
      try {
        if (column.meta_data) {
          const metadata = typeof column.meta_data === 'string'
            ? JSON.parse(column.meta_data)
            : column.meta_data;
          return metadata.type === 'flujo_bot';
        }
        return false;
      } catch (error) {
        console.error('Error procesando meta_data en flujoBotColumns:', error, column.meta_data);
        return false;
      }
    });
    debugLog(`Columnas tipo "flujo_bot" para boardId ${boardId}: ${flujoBotColumns.length}`);

    // Decidir qué columnas usar
    const columnsToUse = flujoBotColumns.length > 0 ? flujoBotColumns : columnsForCurrentBoard;
    debugLog(`Columnas a usar (columnsToUse): ${columnsToUse.length}`);

    if (flujoBotColumns.length === 0 && columnsForCurrentBoard.length > 0) {
      debugLog('No hay columnas "flujo_bot", usando todas las del tablero.');
    }

    // Obtener los IDs de las columnas que pertenecen a este tablero
    const columnIds = columnsToUse.map(col => col.id.toString());
    debugLog(`IDs de columnas para este tablero: [${columnIds.join(', ')}]`);

    // Filtrar tickets que pertenecen a las columnas de este tablero
    const boardTickets = allTickets.filter(ticket =>
      ticket &&
      ticket.column_id &&
      columnIds.includes(ticket.column_id.toString())
    );
    debugLog(`Total de tickets para este tablero: ${boardTickets.length}`);

    // Ordenar columnas por posición
    const sortedColumns = [...columnsToUse].sort((colA, colB) => {
      const posA = parseInt(colA.position, 10) || 0;
      const posB = parseInt(colB.position, 10) || 0;
      return posA - posB;
    });

    // Formatear columnas con sus tickets
    return sortedColumns.map(column => {
      // Filtrar tickets que pertenecen a esta columna
      const columnTickets = boardTickets.filter(ticket =>
        String(ticket.column_id) === String(column.id)
      );

      debugLog(`Columna ${column.name} (ID: ${column.id}): ${columnTickets.length} tickets`);

      return {
        id: column.id.toString(),
        name: column.name,
        position: column.position,
        tickets: columnTickets,
        meta_data: column.meta_data,
        board_id: column.board_id
      };
    });
  }, [boardId, allColumns, allTickets, debugLog]);

  if (!boardId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Por favor, seleccione un tablero.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: isDetailView ? 0 : 2 }}>
      {!isDetailView && board && (
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          {board.name || 'Tablero FlujoBot'}
        </Typography>
      )}

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <KanbanRefreshButton
          isRefreshDisabled={isRefreshDisabled}
          countdown={countdown}
          onRefresh={handleRefresh}
        />
      </Box>

      {/* Renderizamos el tablero sin funcionalidad de arrastrar y soltar y sin mostrar tickets individuales */}
      <Box sx={{ display: 'flex', flexWrap: 'nowrap', overflowX: 'auto', pb: 2 }}>
        {formattedColumns.map((column) => (
          <Box
            key={column.id}
            sx={{
              width: 280,
              minWidth: 280,
              mx: 1,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
              borderRadius: 1,
              boxShadow: 1,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              height: 'fit-content'
            }}
          >
            {/* Encabezado de columna */}
            <Box sx={{
              p: 1.5,
              bgcolor: 'grey.100',
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" fontWeight="500">
                  {column.name}
                </Typography>
                {/* Eliminamos el contador de tickets del encabezado para tableros flujo_bot */}
              </Box>
            </Box>

            {/* Contenedor central que solo muestra el total de tickets */}
            <Box sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 120,
              bgcolor: 'background.default',
              borderRadius: 1,
              m: 1,
              boxShadow: 'inset 0 0 5px rgba(0,0,0,0.1)'
            }}>
              <Typography
                variant="h3"
                fontWeight="bold"
                color="primary.main"
                sx={{
                  textAlign: 'center',
                  mb: 0.5
                }}
              >
                {column.tickets ? column.tickets.length : 0}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 500
                }}
              >
                Tickets
              </Typography>
            </Box>
          </Box>
        ))}

        {formattedColumns.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center', width: '100%' }}>
            <Typography variant="body1" color="text.secondary">
              No hay columnas disponibles para este tablero.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FlujoBotKanbanView;
